import React from 'react';
import * as Yup from 'yup';
import { Form, FormikProps, withFormik } from 'formik';

// Utils
import { formatMessage, FormattedMessage } from '../../../../utils/i18n';

// Components
import { Button } from '@capintel/component-lib/button';
import { SecondaryLink } from '../../../components/buttons';
import { FormInput } from '../../../components/forms';
import Modal from '@/views/components/modals/AccessibleModal';

type FormProps = {
  code: string;
};

type Props = FormikProps<FormProps> & {
  action: (code: string) => Promise<void>;
  children: (openModal: () => void, closeModal: () => void) => React.ReactNode;
  description: string;
};

type State = {
  show: boolean;
};

const canSubmit = (props: Props) => {
  const { values, initialValues, isSubmitting, errors } = props;

  const hasErrors = Object.keys(errors).length > 0;
  const isDirty = values !== initialValues;

  return !hasErrors && isDirty && !isSubmitting;
};
export class TwoFactorProtectedAction extends React.Component<Props, State> {
  readonly state: State = {
    show: false,
  };

  componentDidUpdate(prevProps: Props) {
    const { success: wasSuccess = false } = prevProps.status || {};
    const { success: isSuccess = false } = this.props.status || {};
    if (isSuccess && !wasSuccess) {
      this.closeModal();
    }
  }

  openModal = () => {
    this.setState({ show: true });
  };

  closeModal = () => {
    this.props.resetForm();
    this.setState({ show: false });
  };

  render() {
    const { children, description } = this.props;
    const { show } = this.state;

    return (
      <>
        {children(this.openModal, this.closeModal)}
        <Modal centered show={show} backdrop="static" className="two-factor-protected-action two-factor-container modal">
          <Modal.Body>
            <h3 className="two-factor-auth-header">
              <FormattedMessage id="TwoFactorAuthentication.Headers.EnterYourConfirmationCode" />
            </h3>
            <p className="two-factor-auth-text">{description}</p>
            <Form>
              <FormInput
                name="code"
                type="text"
                label={formatMessage('TwoFactorAuthentication.Headers.ConfirmationCode')}
                autoComplete="off"
                className="two-factor-auth-input mt-4"
                placeholder={formatMessage('TwoFactorAuthentication.Headers.EnterYourConfirmationCode')}
              />
              <div className="two-factor-actions static">
                <Button variant="primary" loading={this.props.isSubmitting} type="submit" disabled={!canSubmit(this.props)}>
                  <FormattedMessage id="TwoFactorAuthentication.Buttons.Next" />
                </Button>
                <SecondaryLink className="two-factor-cancel" onClick={this.closeModal}>
                  <FormattedMessage id="TwoFactorAuthentication.Buttons.Cancel" />
                </SecondaryLink>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </>
    );
  }
}

const TwoFactorProtection = Yup.object().shape({
  code: Yup.string().required(),
});

const formikWrapper = withFormik<Props, FormProps>({
  validationSchema: TwoFactorProtection,
  mapPropsToValues: () => ({ code: '' }),
  handleSubmit: ({ code }, { props, setStatus, setSubmitting }) => {
    props
      .action(code)
      .then(() => {
        setStatus({ success: true });
      })
      .finally(() => {
        setSubmitting(false);
      });
  },
});

export default formikWrapper(TwoFactorProtectedAction) as any;
